import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@fikasio/react-ui-components';
import FilterButton from 'components/UI/FilterButton';
import filterButtonStyles from 'components/UI/FilterButton.module.css';
import FilterModal from 'components/UI/FilterModal';
import FilterSelect from 'components/UI/FilterSelect';
import UserAvatar from 'components/UI/UserAvatar';
import { DueDatePreset } from 'components/filters/dueDatePreset';
import { useAuth } from 'services/login/hooks';
import { useCurrentOrganizationUsersQuery } from 'services/organizations/api';
import { assigneeDisplayName, withCurrentAssignableUser } from 'services/users/assignable';
import {
  DEFAULT_TASKS_FILTER_DRAFT,
  TasksFilterDraft,
  UNASSIGNED_FILTER_VALUE,
} from '../utils';
import styles from './TasksFilters.module.css';

interface TasksFiltersProps {
  defaults?: TasksFilterDraft;
  onApply: (value: TasksFilterDraft) => void;
  showAssigneeFilter?: boolean;
  showCompletionFilter?: boolean;
  showDueDateFilter?: boolean;
  value: TasksFilterDraft;
}

const assigneesDiffer = (left: string[], right: string[]) => (
  left.length !== right.length || left.some((id, index) => id !== right[index])
);

const countActiveFilters = (
  value: TasksFilterDraft,
  defaults: TasksFilterDraft,
  options: {
    showAssigneeFilter?: boolean;
    showCompletionFilter?: boolean;
    showDueDateFilter?: boolean;
  },
) => {
  let count = 0;
  if (options.showCompletionFilter && value.completion !== defaults.completion) {
    count += 1;
  }
  if (options.showDueDateFilter && value.dueDate !== defaults.dueDate) {
    count += 1;
  }
  if ((value.importance ?? 'ALL') !== (defaults.importance ?? 'ALL')) {
    count += 1;
  }
  if (options.showAssigneeFilter && assigneesDiffer(value.assignees, defaults.assignees)) {
    count += 1;
  }
  return count;
};

const TasksFiltersForm = ({
  defaults = DEFAULT_TASKS_FILTER_DRAFT,
  includeAssignees,
  onApply,
  showCompletionFilter,
  showDueDateFilter,
  value,
}: TasksFiltersProps & { includeAssignees: boolean }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<TasksFilterDraft>(value);
  const { user: currentUser } = useAuth();
  const { data: assignableUsers } = useCurrentOrganizationUsersQuery();
  const users = withCurrentAssignableUser(assignableUsers, currentUser);

  const open = () => {
    setDraft(value);
    setIsOpen(true);
  };

  const toggleAssignee = (assigneeId: string) => {
    setDraft(current => ({
      ...current,
      assignees: current.assignees.includes(assigneeId)
        ? current.assignees.filter(id => id !== assigneeId)
        : [...current.assignees, assigneeId],
    }));
  };

  return (
    <div className={filterButtonStyles.anchor}>
      <FilterButton
        activeCount={countActiveFilters(value, defaults, {
          showAssigneeFilter: includeAssignees,
          showCompletionFilter,
          showDueDateFilter,
        })}
        isOpen={isOpen}
        onClick={open}
      />
      <FilterModal
        onApply={() => {
          onApply(draft);
          setIsOpen(false);
        }}
        onClear={() => {
          setDraft(defaults);
          onApply(defaults);
        }}
        onHide={() => setIsOpen(false)}
        show={isOpen}
      >
        {
          showCompletionFilter && (
            <FilterSelect
              label={t('status')}
              onChange={completion => setDraft(current => ({
                ...current,
                completion: completion as TasksFilterDraft['completion'],
              }))}
              options={[
                { color: '#607d8b', icon: 'square', label: t('incompleteTasks'), value: 'INCOMPLETE' },
                { color: '#4caf50', icon: 'check', label: t('completeTasks'), value: 'COMPLETE' },
                { color: '#9e9e9e', icon: 'times', label: t('cancelledTasks'), value: 'CANCELLED' },
                { color: '#5c6bc0', icon: 'archive', label: t('archivedTasks'), value: 'ARCHIVED' },
                { color: '#607d8b', icon: 'list', label: t('allTasks'), value: 'ALL' },
              ]}
              value={draft.completion}
            />
          )
        }
        {
          showDueDateFilter && (
            <FilterSelect
              label={t('dueDate')}
              onChange={dueDate => setDraft(current => ({
                ...current,
                dueDate: dueDate as DueDatePreset,
              }))}
              options={[
                { color: '#607d8b', icon: 'calendar-alt', label: t('anyDueDate'), value: 'ALL' },
                { color: '#2e7d32', icon: 'calendar-alt', label: t('dueToday'), value: 'FOR_TODAY' },
                { color: '#f5a623', icon: 'calendar-alt', label: t('dueThisWeek'), value: 'FOR_THISWEEK' },
                { color: '#c62828', icon: 'triangle-exclamation', label: t('overdue'), value: 'OVERDUE' },
                { color: '#cecece', icon: 'square', label: t('noDueDate'), value: 'NONE' },
              ]}
              value={draft.dueDate}
            />
          )
        }
        <FilterSelect
          label={t('importance')}
          onChange={importance => setDraft(current => ({
            ...current,
            importance: importance as TasksFilterDraft['importance'],
          }))}
          options={[
            { color: '#607d8b', icon: 'list', label: t('anyImportance'), value: 'ALL' },
            { color: '#c62828', icon: 'caret-up', label: t('importanceHigh'), value: 'high' },
            { color: '#f5a623', icon: 'bars', label: t('importanceMedium'), value: 'medium' },
            { color: '#2e7d32', icon: 'caret-down', label: t('importanceLow'), value: 'low' },
            { color: '#cecece', icon: 'square', label: t('noImportance'), value: 'NONE' },
          ]}
          value={draft.importance ?? 'ALL'}
        />
        {
          includeAssignees && (
            <div>
              <div className="fw-semibold mb-2">{t('assignees')}</div>
              <div className={styles.assigneeList}>
                <label className={styles.assigneeOption}>
                  <input
                    checked={draft.assignees.includes(UNASSIGNED_FILTER_VALUE)}
                    onChange={() => toggleAssignee(UNASSIGNED_FILTER_VALUE)}
                    type="checkbox"
                  />
                  <Icon
                    name="user"
                    style={{
                      color: '#cecece',
                      flexShrink: 0,
                      height: 24,
                      width: 24,
                    }}
                  />
                  <span className={styles.assigneeName}>{t('unassigned')}</span>
                </label>
                {
                  users.map(user => (
                    <label
                      className={styles.assigneeOption}
                      key={user.id}
                    >
                      <input
                        checked={draft.assignees.includes(user.id)}
                        onChange={() => toggleAssignee(user.id)}
                        type="checkbox"
                      />
                      <UserAvatar
                        alt=""
                        hasProfilePicture={user.hasProfilePicture}
                        modifiedAt={user.modifiedAt}
                        size={24}
                        userId={user.id}
                      />
                      <span className={styles.assigneeName}>
                        {assigneeDisplayName(user)}
                      </span>
                    </label>
                  ))
                }
              </div>
            </div>
          )
        }
      </FilterModal>
    </div>
  );
};

const TasksFilters = ({
  defaults,
  onApply,
  showAssigneeFilter,
  showCompletionFilter,
  showDueDateFilter,
  value,
}: TasksFiltersProps) => {
  const includeAssignees = showAssigneeFilter ?? !!(showCompletionFilter || showDueDateFilter);
  const hasFilters = includeAssignees || !!showCompletionFilter || !!showDueDateFilter;

  if (!hasFilters) {
    return null;
  }

  return (
    <TasksFiltersForm
      defaults={defaults}
      includeAssignees={includeAssignees}
      onApply={onApply}
      showAssigneeFilter={showAssigneeFilter}
      showCompletionFilter={showCompletionFilter}
      showDueDateFilter={showDueDateFilter}
      value={value}
    />
  );
};

export default TasksFilters;
